import { Notifications, Reminder } from '@griply/types';
import {
  getDisplayedTaskReminders,
  getRemindersSummaryLabel,
  legacyReminderTimeToReminder,
  resolveTaskReminders,
  shouldShowLegacyReminder,
} from '@griply/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { v4 as uuidv4 } from 'uuid';

export type UseRemindersTriggerOptions = {
  reminders?: Reminder[] | null;
  reminderTime?: number | null;
  timeFormat: TimeFormatOptions;
  // Supplied wherever the summary should show the user's settings default for an empty list.
  notifications?: Notifications;
  hasDate?: boolean;
  hasTime?: boolean;
  onChange: (reminders: Reminder[]) => void;
  openMenu: () => void;
};

export const useRemindersTrigger = ({
  reminders,
  reminderTime,
  timeFormat,
  notifications,
  hasDate,
  hasTime,
  onChange,
  openMenu,
}: UseRemindersTriggerOptions) => {
  const { t } = useTranslation();

  const effectiveReminders = resolveTaskReminders(reminders, reminderTime);
  const displayedReminders = getDisplayedTaskReminders(effectiveReminders, {
    notifications,
    hasDate: !!hasDate,
    hasTime: !!hasTime,
  });
  const summaryLabel = getRemindersSummaryLabel(
    displayedReminders,
    t,
    (offset) => parseDateTime(offset, timeFormat),
    timeFormat,
  );

  // Opening the menu migrates a legacy `reminderTime`; the shared write layer then clears it.
  const openWithMigration = useCallback(() => {
    if (shouldShowLegacyReminder(reminders, reminderTime)) {
      onChange([legacyReminderTimeToReminder(reminderTime, uuidv4())]);
    }
    openMenu();
  }, [reminders, reminderTime, onChange, openMenu]);

  return { effectiveReminders, summaryLabel, openWithMigration };
};
